import React, { useCallback, useEffect, useState } from 'react';
import store from './journalStore';
import Calendar from './Calendar';
import compressImage from './compressImage';
import PhotoImg from './PhotoImg';
import Slideshow from './Slideshow';
import VoiceNotes from './VoiceNotes';

/* The journal uses the page passcode (passed in) to authorize the API.
   Each day shows its text notes AND its photos; the same photos also live
   in the Gallery tab. */

const LS_ME = 'anusha_journal_me';
const AUTHORS = ['Tanush', 'Anusha'];
const MOODS = ['💕', '😂', '🥹', '🔥', '🌸', '✨', '🥰', '😭'];

const pad = (n) => String(n).padStart(2, '0');
const ymd = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

const prettyDate = (ds) => {
  const [y, m, dd] = ds.split('-').map(Number);
  return new Date(y, m - 1, dd).toLocaleDateString(undefined, {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
  });
};
const prettyTime = (iso) => {
  try {
    return new Date(iso).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  } catch {
    return '';
  }
};

export default function Journal({ passcode }) {
  const [cursor, setCursor] = useState(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1);
  });
  const [selected, setSelected] = useState(() => ymd(new Date()));
  const [marked, setMarked] = useState(() => new Set());
  const [entries, setEntries] = useState([]);
  const [dayPhotos, setDayPhotos] = useState([]);
  const [loadingDay, setLoadingDay] = useState(false);

  const [me, setMe] = useState(() => localStorage.getItem(LS_ME) || AUTHORS[0]);
  const [text, setText] = useState('');
  const [mood, setMood] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const [photoBusy, setPhotoBusy] = useState('');
  const [dayLb, setDayLb] = useState(null);

  const refreshDates = useCallback(async () => {
    try {
      const [eds, pms, vms] = await Promise.all([
        store.dates(passcode),
        store.photos(passcode),
        store.voices(passcode),
      ]);
      setMarked(new Set([...eds, ...pms.map((p) => p.date), ...vms.map((v) => v.date)]));
    } catch (e) {
      /* ignore */
    }
  }, [passcode]);

  const loadDay = useCallback(
    async (date) => {
      setLoadingDay(true);
      try {
        const [es, ps] = await Promise.all([store.entries(passcode, date), store.photos(passcode, date)]);
        setEntries(es);
        setDayPhotos(ps);
      } catch (e) {
        setEntries([]);
        setDayPhotos([]);
      } finally {
        setLoadingDay(false);
      }
    },
    [passcode]
  );

  useEffect(() => {
    if (!passcode) return;
    refreshDates();
    loadDay(selected);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [passcode]);

  const onSelect = useCallback(
    (ds) => {
      setSelected(ds);
      loadDay(ds);
    },
    [loadDay]
  );

  const prevMonth = () => setCursor((c) => new Date(c.getFullYear(), c.getMonth() - 1, 1));
  const nextMonth = () => setCursor((c) => new Date(c.getFullYear(), c.getMonth() + 1, 1));

  const addNote = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setSaving(true);
    setErr('');
    try {
      await store.add(passcode, { date: selected, author: me, text: text.trim(), mood });
      setText('');
      setMood('');
      await loadDay(selected);
      await refreshDates();
    } catch (e2) {
      setErr(e2.message || 'Could not save.');
    } finally {
      setSaving(false);
    }
  };

  const delNote = async (id) => {
    try {
      await store.remove(passcode, selected, id);
      await loadDay(selected);
      await refreshDates();
    } catch (e) {
      /* ignore */
    }
  };

  const onAddPhotos = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length) return;
    setErr('');
    let done = 0;
    for (const f of files) {
      done += 1;
      setPhotoBusy(`Adding ${done}/${files.length}…`);
      try {
        // eslint-disable-next-line no-await-in-loop
        const { base64 } = await compressImage(f);
        // eslint-disable-next-line no-await-in-loop
        await store.addPhoto(passcode, { date: selected, author: me, base64 });
      } catch (e2) {
        setErr(e2.message || 'Photo upload failed.');
      }
    }
    setPhotoBusy('');
    await loadDay(selected);
    await refreshDates();
  };

  const closeDayShow = () => {
    setDayLb(null);
    loadDay(selected);
    refreshDates();
  };

  const chooseMe = (a) => {
    setMe(a);
    localStorage.setItem(LS_ME, a);
  };

  const isToday = selected === ymd(new Date());

  return (
    <div className="aj-open">
      <Calendar
        cursor={cursor}
        selected={selected}
        marked={marked}
        onSelect={onSelect}
        onPrev={prevMonth}
        onNext={nextMonth}
      />

      <div className="aj-day">
        <div className="aj-day-head">{prettyDate(selected)}</div>

        <form className="aj-compose" onSubmit={addNote}>
          <div className="aj-who">
            <span className="aj-who-label">Writing as</span>
            {AUTHORS.map((a) => (
              <button
                type="button" key={a}
                className={`aj-chip${me === a ? ' is-on' : ''}`}
                onClick={() => chooseMe(a)}
              >
                {a}
              </button>
            ))}
          </div>

          <textarea
            className="aj-textarea"
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={`What do you want to remember about ${isToday ? 'today' : 'this day'}? 💭`}
            rows={3}
          />

          <div className="aj-compose-row">
            <div className="aj-moods">
              {MOODS.map((m) => (
                <button
                  type="button" key={m}
                  className={`aj-mood${mood === m ? ' is-on' : ''}`}
                  onClick={() => setMood(mood === m ? '' : m)}
                  aria-label={`mood ${m}`}
                >
                  {m}
                </button>
              ))}
            </div>
            <button type="submit" className="aj-btn aj-btn-sm" disabled={saving || !text.trim()}>
              {saving ? 'Saving…' : 'Save note 💌'}
            </button>
          </div>
          {err && <div className="aj-err">{err}</div>}
        </form>

        <div className="aj-day-photos">
          <div className="aj-day-photos-head">
            <span className="aj-section-label">📷 Photos {dayPhotos.length ? `(${dayPhotos.length})` : ''}</span>
            <label className={`aj-photo-add${photoBusy ? ' is-busy' : ''}`}>
              {photoBusy || '➕ add'}
              <input type="file" accept="image/*" multiple hidden onChange={onAddPhotos} disabled={!!photoBusy} />
            </label>
          </div>
          {dayPhotos.length > 0 && (
            <div className="aj-day-photo-strip">
              {dayPhotos.map((p, i) => (
                <button
                  type="button" key={p.id} className="aj-day-photo"
                  onClick={() => setDayLb(i)} aria-label="open photo"
                >
                  <PhotoImg passcode={passcode} id={p.id} className="aj-photo-thumb" alt={p.caption || 'memory'} />
                </button>
              ))}
            </div>
          )}
        </div>

        <VoiceNotes passcode={passcode} date={selected} me={me} onChanged={refreshDates} />

        <div className="aj-entries">
          {loadingDay ? (
            <div className="aj-muted">Loading…</div>
          ) : entries.length === 0 && dayPhotos.length === 0 ? (
            <div className="aj-muted">Nothing here yet — write a note or add a photo. 💕</div>
          ) : (
            entries.map((en) => (
              <div className={`aj-entry aj-author-${(en.author || '').toLowerCase()}`} key={en.id}>
                <div className="aj-entry-top">
                  <span className="aj-entry-author">
                    {en.mood ? `${en.mood} ` : ''}
                    {en.author}
                  </span>
                  <span className="aj-entry-time">{prettyTime(en.createdAt)}</span>
                  <button
                    type="button" className="aj-del"
                    onClick={() => delNote(en.id)} aria-label="Delete note"
                  >
                    ×
                  </button>
                </div>
                <div className="aj-entry-text">{en.text}</div>
              </div>
            ))
          )}
        </div>
      </div>

      {dayLb != null && (
        <Slideshow
          passcode={passcode}
          photos={dayPhotos}
          startIndex={dayLb}
          autoplay={false}
          onClose={closeDayShow}
        />
      )}
    </div>
  );
}
